import {useEffect, useRef} from 'react';
import {useMap} from 'react-leaflet';
import L from 'leaflet';
import {NAVY} from '../../tokens.js';
import {cleCommune} from '../../selectors/index.js';
import COMMUNES from './communes-nantes-metropole.geojson.js';
import {couleurPart} from './carte.selectors.js';

/**
 * Limites des communes (surcouche `communes=1`) et aplat par commune (`style=aplat`).
 * `parts` : Map<clé de commune, {nb, enDefaut, part}> (partsParCommune) ; null hors aplat.
 * Couche vectorielle dans un pane sous le canvas des PAV (zIndex 350 < 400).
 */
function texteTooltip(nom, c) {
    return c
        ? `${nom} : ${Math.round(c.part * 100)} % en défaut (${c.enDefaut} / ${c.nb} PAV)`
        : `${nom} : aucun PAV dans la sélection`;
}

function styleCommune(feature, limites, parts) {
    const base = {color: NAVY, weight: limites ? 1.5 : 0.5, opacity: limites ? 0.75 : 0.3, dashArray: limites ? '4 3' : null, fill: !!parts, fillOpacity: 0};
    if (!parts) return base;
    const c = parts.get(cleCommune(feature.properties.nom));
    return {...base, fillColor: c ? couleurPart(c.part) : '#F6F8F4', fillOpacity: c ? 0.7 : 0.35};
}

export default function CarteCommunesLayer({limites, parts}) {
    const map = useMap();
    const couche = useRef(null);
    const avecParts = !!parts;
    const partsRef = useRef(parts);
    partsRef.current = parts;
    const limitesRef = useRef(limites);
    limitesRef.current = limites;

    // La couche est créée une fois par mode (limites seules / aplat) ; les parts sont mises à jour sur place (curseur).
    useEffect(() => {
        if (!limites && !avecParts) return undefined;
        if (!map.getPane('carteCommunes')) {
            const pane = map.createPane('carteCommunes');
            pane.style.zIndex = 350;
        }
        const layer = L.geoJSON(COMMUNES, {
            pane: 'carteCommunes',
            interactive: avecParts,
            style: (feature) => styleCommune(feature, limitesRef.current, partsRef.current),
            onEachFeature: (feature, l) => {
                if (!avecParts) return;
                l.bindTooltip(texteTooltip(feature.properties.nom, partsRef.current && partsRef.current.get(cleCommune(feature.properties.nom))), {sticky: true});
            },
        });
        layer.addTo(map);
        couche.current = layer;
        return () => {
            map.removeLayer(layer);
            couche.current = null;
        };
    }, [map, limites, avecParts]);

    useEffect(() => {
        const layer = couche.current;
        if (!layer || !parts) return;
        layer.setStyle((feature) => styleCommune(feature, limitesRef.current, parts));
        layer.eachLayer((l) => {
            const nom = l.feature.properties.nom;
            if (l.getTooltip()) l.setTooltipContent(texteTooltip(nom, parts.get(cleCommune(nom))));
        });
    }, [parts]);

    return null;
}
